import { Box, Button, Stack, TextField, Typography } from "@mui/material";

import * as yup from "yup";
import { useFormik } from "formik";
import { useDialogs } from "@toolpad/core/useDialogs";

import { Notificacion } from "./Notificacion";
import { RootState, store } from "@store/store";
import { useDispatch, useSelector } from "react-redux";
import { ExternalReviewComments } from "@interfaces/slices";
import { setDialogConfig } from "@store/slices/componentsSlice";
import { setApplicationStateCode } from "@store/slices/appSlice";
import { useLazyGetModulesByCodeQuery } from "@services/api/roles";
import { usePostCommentByAppMutation, usePostStatusApplicationMutation } from "@services/api/applications";

export const ObservacionesRevisionExterna = (props: ExternalReviewComments) => {
    const dialogs = useDialogs();
    const dispatch = useDispatch();

    const applicationSelected = (state = store.getState()) => state.app.applicationSelected;

    const userSession = useSelector((state: RootState) => state.app.session.user);

    const [triggerPostComment] = usePostCommentByAppMutation();
    const [triggerGetModulesByCode] = useLazyGetModulesByCodeQuery();
    const [triggerPostStatusApplication] = usePostStatusApplicationMutation();

    const formik = useFormik({
        initialValues: {
            comment: "",
        },
        validationSchema: yup.object().shape({
            comment:
                props.type === "RETURN_WITH_COMMENTS"
                    ? yup.string().notRequired()
                    : yup.string().required("Comentario requerido"),
        }),
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setSubmitting(true);

                if (values.comment.trim().length !== 0) {
                    const comment =
                        props.docId !== undefined
                            ? `${props.docId}|Documento ${props.docType} rechazado: ${values.comment}`
                            : `Se regreso documentación con las siguientes observaciones: ${values.comment}`;
                    const module = await triggerGetModulesByCode("SBDOCEX").unwrap();
                    await triggerPostComment({
                        applicationId: applicationSelected(store.getState()).id ?? "0-0-0-0-0",
                        payload: {
                            user_id: userSession.id,
                            comment: comment,
                            module_id: module?.id,
                        },
                    }).unwrap();
                }

                if (props.type === "RETURN_WITH_COMMENTS") {
                    await triggerPostStatusApplication({
                        applicationId: applicationSelected(store.getState()).id ?? "0-0-0-0-0",
                        userName:
                            userSession?.name
                                ?.concat(" ", userSession.last_name ?? "", " ", userSession.last_name_2 ?? "")
                                .trim() ?? "",
                        statusCode: "CSEVD",
                        disableSequenceCheck: true,
                    }).unwrap();

                    dispatch(setApplicationStateCode("CSEVD"));
                }

                props.onConfirmClick?.();
                await dialogs.open(Notificacion, {
                    type: "SUCCESS",
                    text: "Documentos contractuales retornados con observaciones.",
                });
            } catch (error) {
            } finally {
                setSubmitting(false);
            }
        },
    });

    const handleCancelClicked = () => {
        props.onBackClick?.();
        dispatch(setDialogConfig());
    };

    return (
        <Box margin={4} maxWidth={"350px"}>
            <Stack textAlign={"center"} rowGap={2}>
                <Typography variant="body1" component="span" fontWeight={600} color="main3">
                    Está seguro que quiere{" "}
                    <Typography variant="body1" component={"span"} fontWeight={600} color="main4">
                        {props.type === "RETURN_WITH_COMMENTS" ? "regresar con observaciones" : "rechazar"}
                    </Typography>{" "}
                    {props.type === "RETURN_WITH_COMMENTS" ? "al cliente" : "el documento:"}
                </Typography>

                {props.type === "REJECT_DOCUMENT" && (
                    <>
                        <Typography variant="body1" fontWeight={600} color={"#528CD6"}>
                            {props.docType}
                        </Typography>
                        <Typography variant="body1" component="span" fontWeight={600} color="main3">
                            Del cliente
                        </Typography>
                    </>
                )}

                <Box>
                    <Typography variant="body1" fontWeight={600} color={"#528CD6"}>
                        {applicationSelected(store.getState()).comercial_name}
                    </Typography>
                </Box>
                <Typography variant="body1" fontWeight={400} color="main3">
                    {props.type === "REJECT_DOCUMENT"
                        ? "Provea de una breve descripción del motivo de rechazo:"
                        : "Si lo deseas, puedes agregar un comentario extra:"}
                </Typography>
                <TextField
                    fullWidth
                    name="comment"
                    label={"Escribe el motivo"}
                    required={props.type === "REJECT_DOCUMENT"}
                    autoComplete="off"
                    multiline
                    rows={3}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.comment}
                    error={formik.touched.comment && Boolean(formik.errors.comment)}
                    helperText={formik.touched.comment && formik.errors.comment}
                />
            </Stack>
            <Stack direction={"row"} display={"flex"} justifyContent={"space-around"} mt={2}>
                <Button variant="blue_outlined" onClick={handleCancelClicked} disabled={formik.isSubmitting}>
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    color="secondary3"
                    onClick={() => formik.submitForm()}
                    loading={formik.isSubmitting}
                >
                    {props.type === "REJECT_DOCUMENT" ? "Rechazar" : "Regresar"}
                </Button>
            </Stack>
        </Box>
    );
};
